/**
 * DOCUMENTATION:
 * Component: DesignPatternsReact
 * Purpose: Educational page teaching Higher-Order Components (Decorator) and Compound Components.
 * * * Note for Junior Developers:
 * - HOCs: A function that takes a component and returns a new component. Think of it as the
 * "Decorator" pattern for React. It’s great for cross-cutting concerns like Auth or Analytics.
 * - Compound Components: A pattern where several components work together to form a unit,
 * sharing implicit state. (e.g., <Select> and <Option>). This prevents "Prop Drilling."
 */

import React, { useState, useEffect, useMemo, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsReact() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const reactPatterns = useMemo(() => [
        {
            name: "HOC (Higher-Order Component)",
            intent: "A pattern used to share logic between components without repeating code. It transforms a component into another component.",
            useCase: "Authorization checks, adding 'Loading' states, or injecting theme/context data."
        },
        {
            name: "Compound Components",
            intent: "Allow components to share state and logic implicitly, providing a more expressive and flexible API for the consumer.",
            useCase: "Tabs, Accordions, Dropdown Menus, and complex Form layouts."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. Higher-Order Component (The Decorator)",
            code: `// ✅ The HOC: Adds a 'loading' prop logic to any component\nconst withLoader = (WrappedComponent) => {\n  return ({ isLoading, ...props }) => {\n    if (isLoading) return <div>Loading data...</div>;\n    return <WrappedComponent {...props} />;\n  };\n};\n\n// ✅ Usage:\nconst UserProfile = ({ name }) => <h1>{name}</h1>;\nconst ProfileWithLoader = withLoader(UserProfile);\n\n// In the UI:\n<ProfileWithLoader isLoading={true} name="Kofi" />`,
            desc: "HOCs are essentially the Decorator pattern. They 'wrap' a component to give it new powers (like a loading spinner) without modifying the original component's code."
        },
        {
            title: "2. Compound Components (Implicit State)",
            code: `// ✅ The Pattern: Parent (Tabs) manages state, Children (Tab) use it\nconst TabsContext = React.createContext();\n\nconst Tabs = ({ children }) => {\n  const [activeTab, setActiveTab] = useState(0);\n  return (\n    <TabsContext.Provider value={{ activeTab, setActiveTab }}>\n      {children}\n    </TabsContext.Provider>\n  );\n};\n\nconst Tab = ({ index, children }) => {\n  const { activeTab, setActiveTab } = useContext(TabsContext);\n  return (\n    <button \n      onClick={() => setActiveTab(index)}\n      style={{ color: activeTab === index ? 'gold' : 'white' }}\n    >\n      {children}\n    </button>\n  );\n};\n\n// Usage: Very clean and readable!\n<Tabs>\n  <Tab index={0}>Home</Tab>\n  <Tab index={1}>Settings</Tab>\n</Tabs>`,
            desc: "Compound components prevent 'Prop Drilling.' Instead of passing the 'activeTab' index manually to every child, the children reach into the shared Context to get what they need."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    PATTERNS IN REACT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Modern UI Architecture
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            While classical design patterns were written for languages like C++ and Java, React has its own set of structural patterns that solve the same problems: <strong>reusability, state management, and separation of concerns</strong>.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>The React Toolbox</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {reactPatterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
